import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCreateEmployeeProject } from '../../../hooks/useEmployees';
import { useProjects } from '../../../hooks/useProjects';
import { IconLoader2, IconX } from '@tabler/icons-react';
import { toast } from 'react-hot-toast';

export default function AssignProjectModal({ isOpen, onClose, employee }) {
    const { user } = useAuth();
    const orgId = user?.orgId;

    // Fetch projects
    const { data: projects = [], isLoading: loadingProjects } = useProjects(orgId);

    // Mutation
    const createAssignment = useCreateEmployeeProject();

    const [formData, setFormData] = useState({
        project: '',
        role_on_project: '',
        allocation_start: new Date().toISOString().split('T')[0],
        allocation_end: '',
        default_loe_percent: '100',
        is_primary: false
    });

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.project) {
            toast.error('Please select a project');
            return;
        }

        try {
            // Payload preparation: ensure empty string becomes null for dates
            const payload = {
                ...formData,
                employee: employee.id,
                allocation_end: formData.allocation_end || null,
            };

            await createAssignment.mutateAsync({
                data: payload,
                orgId
            });
            toast.success('Project assigned successfully');
            onClose();
            // Reset form
            setFormData({
                project: '',
                role_on_project: '',
                allocation_start: new Date().toISOString().split('T')[0],
                allocation_end: '',
                default_loe_percent: '100',
                is_primary: false
            });
        } catch (error) {
            console.error("Assignment error:", error);
            const msg = error?.response?.data?.message ||
                error?.response?.data?.non_field_errors?.[0] ||
                error?.response?.data?.detail ||
                // Check for field specific errors
                (error?.response?.data && Object.values(error.response.data)[0]?.[0]) ||
                error.message ||
                'Failed to assign project';
            toast.error(msg);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-bg-dark rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 border border-border">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-bg-light">
                    <div>
                        <h3 className="text-lg font-bold text-text-primary">Assign Project</h3>
                        <p className="text-sm text-text-secondary">Assign {employee?.name} to a project</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-border/50 rounded-full transition-colors"
                    >
                        <IconX size={20} className="text-text-secondary" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">

                    {/* Project Select */}
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Project <span className="text-danger">*</span></label>
                        <select
                            value={formData.project}
                            onChange={e => setFormData(prev => ({ ...prev, project: e.target.value }))}
                            className="w-full bg-bg-light border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                            disabled={loadingProjects}
                            required
                        >
                            <option value="">Select Project</option>
                            {projects.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Role on Project */}
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Role on Project</label>
                        <input
                            type="text"
                            value={formData.role_on_project}
                            onChange={e => setFormData(prev => ({ ...prev, role_on_project: e.target.value }))}
                            placeholder="e.g. Developer, Lead, etc."
                            className="w-full bg-bg-light border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        />
                    </div>

                    {/* LOE */}
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">LOE (%)</label>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.default_loe_percent}
                            onChange={e => setFormData(prev => ({ ...prev, default_loe_percent: e.target.value }))}
                            className="w-full bg-bg-light border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        />
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-primary mb-2">Start Date</label>
                            <input
                                type="date"
                                value={formData.allocation_start}
                                onChange={e => setFormData(prev => ({ ...prev, allocation_start: e.target.value }))}
                                className="w-full bg-bg-light border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-primary mb-2">End Date</label>
                            <input
                                type="date"
                                value={formData.allocation_end}
                                onChange={e => setFormData(prev => ({ ...prev, allocation_end: e.target.value }))}
                                className="w-full bg-bg-light border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                            />
                            {/* <p className="text-xs text-warning mt-1">Leave empty for now (Backend requires open-ended assignment for timesheets)</p> */}
                        </div>
                    </div>

                    {/* Is Primary */}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="is_primary"
                            checked={formData.is_primary}
                            onChange={e => setFormData(prev => ({ ...prev, is_primary: e.target.checked }))}
                            className="rounded border-border bg-bg-light text-primary focus:ring-primary"
                        />
                        <label htmlFor="is_primary" className="text-sm text-text-primary">Set as Primary Project</label>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 pt-6 border-t border-border mt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-text-primary bg-bg-light border border-border rounded-lg hover:bg-border/50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={createAssignment.isPending}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                        >
                            {createAssignment.isPending && <IconLoader2 size={16} className="animate-spin" />}
                            Assign Project
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
